import React, { memo, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  TextareaAutosize,
  Typography,
} from '@mui/material';

import ThumbSelector from 'components/ThumbSelector';
import MultiSelectButtonGroup from 'components/MultiSelectButtonGroup';
import { debounce } from 'lodash';
import { formatInTimeZone } from 'date-fns-tz';
import { RewindRecording } from 'types/recordings';
import theme from '@wavetrak/theme';
import { RefState } from 'hooks/useRefState';
import { trackEvent } from '@surfline/web-common';
import styles from './CamFeedbackForm.module.scss';
import postData from './postData';

interface Props {
  cameraId: string;
  cameraName: string;
  currentTimestamp?: RefState<Date | undefined>;
  rewindList?: RewindRecording[];
  spotId?: string;
  spotName?: string;
  spotTimezone: string;
  onFormSubmitCallback?: (success: boolean) => void;
  isWavePool?: boolean;
}

const thumbsUpOptions = ['View of the surf', 'Stream quality', 'Something else'];

const thumbsDownOptions = ['Obscured', 'Dirty', 'Stream quality', 'Camera angle', 'Something else'];

export const FEEDBACK_FORM_TIME_FORMAT = "hh:mm aaaaa'm'";

const getSelectedOptions = (selectedOptions: string[], comments?: string) => {
  if (selectedOptions.length > 0) {
    return selectedOptions;
  }
  if (comments) {
    return ['Something else'];
  }
  return undefined;
};

const CamFeedbackForm: React.FC<Props> = ({
  cameraId,
  cameraName,
  currentTimestamp,
  rewindList,
  spotId,
  spotName,
  spotTimezone,
  onFormSubmitCallback,
  isWavePool,
}) => {
  const [thumbsUp, setThumbsUp] = React.useState<boolean | undefined>(undefined);
  const [selectedOptions, setSelectedOptions] = React.useState<string[]>([]);
  const [comments, setComments] = React.useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const timestamp = useMemo(
    () => currentTimestamp?.current.value || new Date(),
    [currentTimestamp],
  );

  const currentTimestampString = useMemo(
    () => formatInTimeZone(timestamp, spotTimezone, FEEDBACK_FORM_TIME_FORMAT),
    [spotTimezone, timestamp],
  );

  const currentRewind = useMemo(() => {
    const currentTime = currentTimestamp?.current.value?.getTime();
    const rewind =
      !!currentTime &&
      rewindList?.find(
        (r) =>
          new Date(r.startDate).getTime() <= currentTime &&
          new Date(r.endDate).getTime() > currentTime,
      );
    return rewind || rewindList?.[rewindList.length - 1];
  }, [currentTimestamp, rewindList]);

  const currentStill = currentRewind?.thumbLargeUrl;

  const commentsSectionRef = React.useRef<HTMLTextAreaElement>(null);

  const debouncedCommentsAdded = useMemo(
    () =>
      debounce(() => {
        setComments(commentsSectionRef.current?.value);
      }, 500),
    [],
  );

  useEffect(
    () => () => {
      debouncedCommentsAdded.cancel();
    },
    [debouncedCommentsAdded],
  );

  useEffect(() => {
    setSelectedOptions([]);
  }, [thumbsUp]);

  const getValues = useCallback(
    () => ({
      rating: {
        good: thumbsUp,
        attributes: getSelectedOptions(selectedOptions, commentsSectionRef.current?.value),
        comments: commentsSectionRef.current?.value || undefined,
      },
      timestamp: timestamp.toISOString(),
      imageLink: currentStill,
      videoLink: currentRewind?.recordingUrl,
      camId: cameraId,
      spotId: isWavePool ? undefined : spotId,
      // spot Id is wavePoolId if isWavePool is true
      wavePoolId: isWavePool ? spotId : undefined,
    }),
    [
      thumbsUp,
      selectedOptions,
      timestamp,
      currentStill,
      currentRewind,
      cameraId,
      spotId,
      isWavePool,
    ],
  );

  const handleFormSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      setIsSubmitting(true);
      const values = getValues();

      const success = await postData(values);

      trackEvent('Camera Feedback Submitted', {
        spotId,
        spotName,
        cameraId,
        cameraName,
        cameraRating: values.rating.good ? 'good' : 'bad',
        cameraProblem: values.rating.good ? [] : values.rating.attributes,
        cameraLikes: values.rating.good ? values.rating.attributes : [],
        cameraComments: values.rating.comments,
      });

      setIsSubmitting(false);

      if (onFormSubmitCallback) {
        onFormSubmitCallback(success);
      }
    },
    [getValues, cameraName, cameraId, spotName, spotId, onFormSubmitCallback],
  );

  const onThumbChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      const thumbsUpValue = value === 'yes';
      setThumbsUp(thumbsUpValue);
      trackEvent('Button Clicked', {
        buttonText: thumbsUpValue ? 'Camera Feedback (Thumbs Up)' : 'Camera Feedback (Thumbs Down)',
        spotId,
        spotName,
        cameraId,
        cameraName,
        pageName: 'Spot Report',
        uiElement: 'Camera Feedback Form',
      });
    },
    [setThumbsUp, spotId, spotName, cameraId, cameraName],
  );

  return (
    <Box className={styles.camFeedbackFormWrapper} data-testid="cam-feedback-form-wrapper">
      <form onSubmit={handleFormSubmit}>
        <Box className={styles.camFeedbackFormSection}>
          <Typography className={styles.camFeedbackFormSectionHeading} variant="subHeadline">
            How would you rate this cam?
          </Typography>
          <ThumbSelector thumbsUp={thumbsUp} onChange={onThumbChange} />
        </Box>
        {thumbsUp !== undefined && (
          <Box className={styles.camFeedbackFormSection} data-testid="cam-feedback-form-inputs">
            <Typography className={styles.camFeedbackFormSectionHeading} variant="subHeadline">
              {thumbsUp ? 'What do you like about this cam?' : 'What seems to be the problem?'}
            </Typography>
            <MultiSelectButtonGroup
              options={thumbsUp ? thumbsUpOptions : thumbsDownOptions}
              setSelectedOptions={setSelectedOptions}
              selectedOptions={selectedOptions}
            />
            <FormControl className={styles.commentsInputWrapper}>
              <Typography className={styles.commentsInputLabel} variant="callout2">
                Comments
              </Typography>
              <TextareaAutosize
                ref={commentsSectionRef}
                className={styles.commentsInput}
                onChange={() => debouncedCommentsAdded()}
                data-testid="comments-input"
                defaultValue=""
                maxLength={500}
                minRows={3}
                placeholder={
                  thumbsUp
                    ? ''
                    : 'Give us a little more detail so we can resolve this issue as quickly as possible.'
                }
              />
            </FormControl>
            <Box className={styles.camImageWrapper}>
              {!!currentStill && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  className={styles.camImage}
                  src={currentStill}
                  alt="current camera frame"
                  data-testid="current-still-img"
                />
              )}
              <Typography
                variant="caption1"
                color={theme.conditions.veryPoor}
                data-testid="current-timestamp"
              >{`${cameraName}, ${currentTimestampString}`}</Typography>
            </Box>
            <Button
              className={styles.submitButton}
              type="submit"
              variant="primary"
              color="secondary"
              size="large"
              disabled={selectedOptions.length === 0 && !comments && !thumbsUp}
              data-testid="submit-button"
            >
              {isSubmitting && (
                <Box className={styles.loadingIcon}>
                  <CircularProgress
                    size={20}
                    sx={{ color: 'grey' }}
                    data-testid="feedback-button-loading"
                  />
                </Box>
              )}
              Send Feedback
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default memo(CamFeedbackForm);
